// MECHANISM 3: AMBIENT OCCLUSION THAT SURVIVES THIS SCENE.
//
// THE HISTORY THIS FILE HAS TO NOT REPEAT
// Babylon's SSAO2 was wired up in M9 and REMOVED because it crushed the frame
// to black. That was not a tuning failure. SSAO2 is a post-process: it
// multiplies the FINAL, fully-shaded pixel. Every surface the player looks at
// here is drawn by a hand-written self-lit ShaderMaterial that never consulted
// Babylon's light rig, so "the pixel" is already the finished sum of key + sky
// fill + bounce — and multiplying that by an occlusion factor darkens the SUN
// as well as the sky, on materials that had no headroom to give. On top of
// that, SSAO2 needs G-buffer normals, and a ShaderMaterial contributes nothing
// to the geometry buffer, so half the scene was occluded against normals that
// did not exist.
//
// So this pass is built the other way round, and it borrows the one mechanism
// this project has already proved works on these materials — sunshadow.js's
// shader injection:
//
//   1. HALF-RES DEPTH PRE-PASS, NEAR FIELD ONLY. Babylon's DepthRenderer
//      renders a packed 32-bit linear depth map for the meshes within AO reach
//      (40 m). It costs no G-buffer and no normals: it needs geometry only, so
//      ShaderMaterials, thin instances, skinned bodies and alpha-cut leaf cards
//      all land in it through machinery the shadow map already exercises daily.
//   2. HORIZON-STYLE AO AT HALF RES, NORMALS RECONSTRUCTED FROM DEPTH. 12 taps
//      on a golden spiral, rotated by a 4x4 ordered tile, normal from the
//      nearest depth neighbours — which is exactly why there is no G-buffer
//      requirement.
//   3. A DEPTH-AWARE 4x4 BLUR sized to that tile, so 12 taps read as occlusion
//      rather than as dither, and edges do not bleed across a silhouette.
//   4. THE AO IS INJECTED INTO THE AMBIENT LOBE OF THE WORLD SHADERS, NOT
//      MULTIPLIED OVER THE FRAME. `vec3 amb = ...` becomes
//      `vec3 amb = ashAO * ...`. A sunlit wall keeps its key light at full
//      strength — only the sky fill it cannot see is taken away. That is both
//      what AO physically is and the reason nothing can go black: the term this
//      pass touches is a fraction of the pixel, and the sun term is untouched.
//      Interiors are the one exception and they are the honest one: every watt
//      in map10kInterior arrives through the aperture model, i.e. all of it is
//      indirect, so there the term multiplies the composed interior light — at
//      its own, lower strength (ashAOP.w).
//
// The injection is anchored on the same stable tokens sunshadow.js uses
// (`vec3 amb = `, `vec3 skyAmb = `, `+ ambUp)`), and this file is installed
// BEFORE it so the two patches compose in a defined order (see engine.js).
//
// Everything is off by default until the AO texture has actually rendered:
// ashAOP.z/w are 0, the injected code early-outs at 1.0, and an unset uniform
// on a ShaderMaterial is 0 — so a failure anywhere in this file renders the
// exact round-006 image rather than a dark one.
//
// WHAT IT MEASURES. Every number below is an INTERLEAVED A/B at gate
// resolution: one boot, one settle, then off -> on -> strong -> off again in
// the same page with nothing but the strength changing, because the beauty-shot
// harness on a loaded box drifts by more than the gate's whole tolerance
// between two captures of the same scene (forest_day's ground band moved 7.7
// luminance between two captures 5 minutes apart with AO switched OFF, which is
// larger than any effect this pass has). The repeat of the off frame at the end
// is what makes the middle rows trustworthy.
//   interior_izba (refs/ours/wip-009-iz-*)   full lum / ground lum
//     off      81.33 / 98.09
//     ship     76.89 / 92.17     <- this file's defaults
//     strong   72.95 / 87.33     (k 0.9, interior 0.8, bias 0.10)
//     off2     81.28 / 97.71     <- drift over the whole run: 0.05 / 0.38
//   town_street (refs/ours/wip-009-ts-*)     full lum / ground lum
//     off     110.66 / 69.04
//     ship    110.65 / 69.13     shotguard: 0 of 23 metrics moved at all
//     strong  110.53 / 68.96
//     off2    110.70 / 69.13
// So: a measured -4.4 frame / -5.9 ground in the izba, and provably nothing in
// the overcast street, whose frame holds no geometry inside the 0.9 m radius.
// Against the DayZ plates (shotguard, izba, AO vs its own control) the izba
// gains full lum err 4.0 -> 0.5, ground lum 27.8 -> 21.9, ground p50 35.7 ->
// 23.7, and adds no regression the AO-off control does not already have.
// The picture gains the wall/floor junction, the ceiling-beam soffits, the door
// reveals and the room corner. Nothing in it goes black: the AO term is floored
// at 0.25 (aoP.w) before it is applied at all, and it is applied to a lobe, not
// to the pixel.
//
// COST (tools/mapbench.mjs, same four stops, ?ao=0 vs on):
//     draw calls  160/166/191/143  ->  300/268/252/265   (budget 500)
//     VRAM        809 MB -> 813 MB, JS heap 304 -> 342 MB, tris unchanged
// The whole cost is the near-field depth pre-pass: ~100-140 extra draws for the
// meshes inside `reach`. If a machine cannot afford it, `reach` (40 m) is the
// dial that trades AO range for draws, and ?ao=0 removes the pass entirely.
//
// KNOBS (URL, so an A/B is one page load and not a rebuild; window.__ashAO
// carries the same fields live, which is how the sweeps above were shot):
//   ?ao=0    off entirely — no depth pass, no procedural textures, no uniforms
//   ?aoK=    hard-surface strength   ?aoKv= vegetation   ?aoKi= interior
//   ?aoR=    world radius (m)   ?aoI=   gain   ?aoB= bias   ?aoF= floor
import { Effect } from '@babylonjs/core/Materials/effect';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { RenderTargetTexture } from '@babylonjs/core/Materials/Textures/renderTargetTexture';
import { ProceduralTexture } from '@babylonjs/core/Materials/Textures/Procedurals/proceduralTexture';
import '@babylonjs/core/Materials/Textures/Procedurals/proceduralTextureSceneComponent';
import { DepthRenderer } from '@babylonjs/core/Rendering/depthRenderer';
import { Vector3, Vector4 } from '@babylonjs/core/Maths/math';

// ---------------------------------------------------------------------------
// 1. THE INJECTION
// ---------------------------------------------------------------------------
// ashAOP: x = VEGETATION strength, y = 1/renderHeight (diagnostic only — the
//         screen UV comes from ashAOVP * vPos, see ashAOFetch),
//         z = AMBIENT strength (hard surfaces), w = INTERIOR strength.
// ashAOVP: the camera's view-projection, i.e. the matrix the AO buffer was
//         rendered with.
// Both strengths are 0 until the pass has produced a frame, and the fetch
// early-outs on that, so an un-bound sampler can never darken anything.
const GLSL = `
uniform sampler2D ashAOMap;
uniform vec4 ashAOP;
uniform mat4 ashAOVP;
float ashAOs = 1.0;
float ashAO = 1.0;
float ashAOV = 1.0;
float ashAOI = 1.0;
// The screen UV is RE-PROJECTED FROM THE WORLD POSITION, not read off
// gl_FragCoord. That is not a stylistic choice: the gl_FragCoord version of
// this function shipped first and produced a frame that was bit-for-bit the
// no-AO frame (measured: mean |delta| 7.4 = the film-grain pass alone, mean
// delta 0.003). The pass was proven alive at the same time by two debug
// branches through the same uniform — a constant 0.5 return moved the izba's
// mean from 81.2 to 23.9, and a lookup at a fixed vec2(0.5) moved it to 50.0 —
// so the injection, the uniform and the texture were all live and only the
// gl_FragCoord.xy * (1/renderWidth) coordinate was landing off the map.
// vPos re-projected through the camera's view-projection cannot: it is the
// same matrix the AO pass itself was rendered with, in NDC, independent of
// render size, hardware scaling and of which framebuffer the main pass is
// drawing into.
float ashAOFetch() {
  if (ashAOP.x < 0.002 && ashAOP.z < 0.002 && ashAOP.w < 0.002) return 1.0;
  vec4 cp = ashAOVP * vec4(vPos, 1.0);
  if (cp.w < 1e-4) return 1.0;
  vec2 suv = cp.xy / cp.w * 0.5 + 0.5;
  return texture2D(ashAOMap, suv).r;
}
`;

// Per-shader anchor -> replacement. The outdoor shaders take AO on the SKY
// AMBIENT lobe only (the sun lobe is never occluded by a screen-space term);
// map10kInterior takes it on the composed light because an interior has no
// direct lobe to protect — the aperture model IS its indirect light.
const AO_FRAGS = {
  map10k: [['vec3 amb = ', 'vec3 amb = ashAO * ']],
  map10kVeg: [['vec3 amb = ', 'vec3 amb = ashAOV * ']],
  map10kBldg: [['vec3 amb = ', 'vec3 amb = ashAO * ']],
  map10kPropGlb: [['vec3 amb = ', 'vec3 amb = ashAO * ']],
  map10kRoad: [['vec3 skyAmb = ', 'vec3 skyAmb = ashAO * ']],
  map10kFence: [['+ ambUp)', '+ ambUp * ashAO)']],
  map10kRail: [['+ ambUp)', '+ ambUp * ashAO)']],
  map10kInterior: [[') * ao * aoC;', ') * ao * aoC * ashAOI;']],
};

const PATCHED = new Set();
const MATS = new Set();
let installed = false;

function patchFragment(key, edits) {
  const store = Effect.ShadersStore;
  const k = `${key}FragmentShader`;
  let src = store[k];
  if (!src || src.indexOf('ashAOP') >= 0) return;
  if (src.indexOf('void main') < 0) return;
  let hit = false;
  for (const [from, to] of edits) {
    if (src.indexOf(from) < 0) continue;
    src = src.split(from).join(to);
    hit = true;
  }
  // An anchor that no longer exists means somebody re-wrote that shader's
  // lighting. Patch nothing rather than declare uniforms nobody reads.
  if (!hit) return;
  src = src.replace('void main', `${GLSL}\nvoid main`);
  src = src.replace(/void main\s*\(\s*(?:void)?\s*\)\s*\{/,
    (m) => `${m}\n  ashAOs = ashAOFetch();\n`
      + '  ashAO = 1.0 - (1.0 - ashAOs) * ashAOP.z;\n'
      + '  ashAOV = 1.0 - (1.0 - ashAOs) * ashAOP.x;\n'
      + '  ashAOI = 1.0 - (1.0 - ashAOs) * ashAOP.w;');
  store[k] = src;
  PATCHED.add(key);
}

// Same reason as sunshadow.js: Babylon caches effects by name+defines, so the
// uniform/sampler names must be in _options BEFORE the effect compiles.
// Chaining onto whatever isReady is already installed keeps both hooks alive.
function hookShaderMaterial() {
  if (ShaderMaterial.prototype.__ashAOHooked) return;
  const orig = ShaderMaterial.prototype.isReady;
  ShaderMaterial.prototype.isReady = function (...args) {
    if (this.__ashAORecv === undefined) {
      const sp = this.shaderPath;
      const frag = typeof sp === 'string' ? sp : sp && sp.fragment;
      this.__ashAORecv = typeof frag === 'string' && PATCHED.has(frag);
      if (this.__ashAORecv) {
        const o = this.options;
        for (const u of ['ashAOP', 'ashAOVP']) {
          if (o.uniforms.indexOf(u) < 0) o.uniforms.push(u);
        }
        if (o.samplers.indexOf('ashAOMap') < 0) o.samplers.push('ashAOMap');
        MATS.add(this);
      }
    }
    return orig.apply(this, args);
  };
  ShaderMaterial.prototype.__ashAOHooked = true;
}

// ---------------------------------------------------------------------------
// 2. THE AO SHADER (half res)
// ---------------------------------------------------------------------------
// camP: x = near, y = far - near, z = 1/m00, w = 1/m11 of the projection.
//       Babylon's PerspectiveFovLH writes clip.z = (f+n)/(f-n) * z - 2fn/(f-n)
//       and depth.vertex stores (clip.z + n) / (n + f), which reduces EXACTLY
//       to (z - n) / (f - n): the map is linear in view-space z, so a tap
//       inverts to metres with one multiply-add and no matrix.
// aoP:  x = world radius (m), y = intensity (exponent gain), z = angle bias,
//       w = hard floor — the darkest AO this pass is ever allowed to return.
// aoQ:  x = fade start (m), y = fade end (m), z/w = texel size.
const AO_FRAG = `
precision highp float;
varying vec2 vUV;
uniform sampler2D depthTex;
uniform vec4 camP;
uniform vec4 aoP;
uniform vec4 aoQ;

float unpackA(vec4 c) {
  return dot(c, vec4(1.0 / (255.0 * 255.0 * 255.0), 1.0 / (255.0 * 255.0), 1.0 / 255.0, 1.0));
}
float rawAt(vec2 uv) { return unpackA(texture2D(depthTex, uv)); }
float zOf(float d) { return camP.x + d * camP.y; }
vec3 viewPos(vec2 uv, float z) {
  return vec3((uv.x * 2.0 - 1.0) * camP.z * z, (uv.y * 2.0 - 1.0) * camP.w * z, z);
}
// INTERLEAVED SAMPLING, not a hash. 12 taps on a disc 58 texels across is a
// noisy estimator however the taps are placed, and a random per-pixel rotation
// spreads that noise over the whole disc — far wider than any blur that is
// still allowed to respect a silhouette. So the rotation is a 4x4 ORDERED
// pattern instead: the sixteen pixels of a tile take sixteen different
// rotations (bit-reversed so neighbours are maximally apart) and the blur
// below averages exactly that tile. 12 taps x 16 rotations = 192 directions
// per tile, resolved by a 4-texel blur rather than a 58-texel one.
float tileIdx(vec2 px) {
  float x = mod(px.x, 4.0);
  float y = mod(px.y, 4.0);
  return mod(x, 2.0) * 8.0 + mod(y, 2.0) * 4.0 + floor(x * 0.5) * 2.0 + floor(y * 0.5);
}

void main(void) {
  float d0 = rawAt(vUV);
  if (d0 > 0.999) { gl_FragColor = vec4(1.0); return; }
  float z0 = zOf(d0);
  float fade = 1.0 - smoothstep(aoQ.x, aoQ.y, z0);
  if (fade < 0.004) { gl_FragColor = vec4(1.0); return; }
  vec3 P = viewPos(vUV, z0);

  // NORMAL FROM DEPTH — the whole reason this pass does not need a G-buffer
  // (and therefore does not care that the world is drawn by ShaderMaterials).
  // The nearer of each opposed neighbour pair wins, so a silhouette edge takes
  // the derivative that stays ON the surface instead of spanning the gap.
  vec2 tx = vec2(aoQ.z, 0.0);
  vec2 ty = vec2(0.0, aoQ.w);
  float zR = zOf(rawAt(vUV + tx));
  float zL = zOf(rawAt(vUV - tx));
  float zU = zOf(rawAt(vUV + ty));
  float zD = zOf(rawAt(vUV - ty));
  vec3 dx = abs(zR - z0) < abs(z0 - zL)
    ? viewPos(vUV + tx, zR) - P : P - viewPos(vUV - tx, zL);
  vec3 dy = abs(zU - z0) < abs(z0 - zD)
    ? viewPos(vUV + ty, zU) - P : P - viewPos(vUV - ty, zD);
  vec3 N = cross(dx, dy);
  float nl = length(N);
  if (nl < 1e-9) { gl_FragColor = vec4(1.0); return; }
  N /= nl;
  // P runs camera -> surface, so a front face has dot(N, P) < 0 whatever the
  // handedness of the projection turns out to be.
  if (dot(N, P) > 0.0) N = -N;

  // Screen radius of the world sphere. Clamped at both ends: a floor of two
  // texels keeps a distant surface from sampling itself, and a ceiling stops a
  // surface 30 cm from the lens from spraying taps over a third of the screen
  // (the world radius shrinks with it, so the falloff stays consistent).
  // The ceiling is 16% of frame height, not the 9% this started at: an interior
  // is 2-6 m deep, so at 9% the clamp was silently cutting the 0.9 m radius
  // down to 0.3 m in exactly the two scenes AO is supposed to own, and a 30 cm
  // reach cannot see a room corner from the middle of a wall.
  vec2 rUV = vec2(0.5 * aoP.x / (z0 * camP.z), 0.5 * aoP.x / (z0 * camP.w));
  float k = min(1.0, 0.16 / max(rUV.y, 1e-6));
  rUV *= k;
  float R = aoP.x * k;
  vec2 rMin = 2.0 * vec2(aoQ.z, aoQ.w);
  if (rUV.y < rMin.y) { rUV = rMin; R = z0 * camP.w * rMin.y * 2.0; }
  float R2 = R * R;

  float ti = tileIdx(gl_FragCoord.xy);
  float ang0 = ti * 0.39269908;
  float occ = 0.0;
  for (int i = 0; i < 12; i++) {
    // the tile index also jitters the RADIUS, so the sixteen pixels of a tile
    // sample sixteen different rings as well as sixteen different bearings
    float fi = float(i) + ti / 16.0 + 0.25;
    float ang = ang0 + fi * 2.39996323;
    float rad = sqrt(min(fi, 11.99) / 12.0);
    vec2 suv = vUV + vec2(cos(ang), sin(ang)) * rad * rUV;
    float ds = rawAt(suv);
    float zs = zOf(ds);
    vec3 v = viewPos(suv, zs) - P;
    float l2 = dot(v, v);
    float l = sqrt(max(l2, 1e-8));
    // horizon term: how far above this surface's own plane the sample sits.
    float h = max(0.0, dot(N, v) / l - aoP.z);
    // range falloff, plus a hard mask on background taps and on samples
    // outside the sphere — an occluder 20 m behind is not an occluder.
    float rangeK = clamp(1.0 - l2 / R2, 0.0, 1.0);
    float valid = step(ds, 0.999) * step(suv.x, 1.0) * step(0.0, suv.x)
      * step(suv.y, 1.0) * step(0.0, suv.y);
    occ += h * rangeK * valid;
  }
  // TRANSMITTANCE, NOT A LINEAR SUBTRACTION. The measured spread of occ across
  // an izba is ~10:1 between an open wall and the tightest ceiling-beam corner,
  // so a linear 1 - k*occ cannot be tuned: the k that puts a corner at 0.7 puts the
  // extreme at 0, which is the SSAO2 failure this pass exists to avoid. exp()
  // is what a visibility integral actually looks like, it saturates instead of
  // clipping, and it can never reach zero. aoP.w then floors it anyway.
  float ao = max(aoP.w, exp(-aoP.y * occ / 12.0));
  ao = mix(1.0, ao, fade);
  gl_FragColor = vec4(ao, ao, ao, 1.0);
}
`;

// Depth-aware 4x4 blur — the other half of the interleaved scheme above, and
// sized to it exactly: it averages the 4x4 tile whose sixteen pixels carry the
// sixteen rotations, which is what turns 12 noisy taps into one smooth
// estimate. It is only 4 texels wide, so it can afford to be depth-aware
// without bleeding occlusion across a silhouette (a plain box blur haloes
// every trunk). The depth weight is a fraction of the sample's own distance,
// so it holds up at 2 m and at 30 m without a second constant.
const BLUR_FRAG = `
precision highp float;
varying vec2 vUV;
uniform sampler2D aoTex;
uniform sampler2D depthTex;
uniform vec4 blurP;

float unpackA(vec4 c) {
  return dot(c, vec4(1.0 / (255.0 * 255.0 * 255.0), 1.0 / (255.0 * 255.0), 1.0 / 255.0, 1.0));
}
float zOf(vec2 uv) { return blurP.z + unpackA(texture2D(depthTex, uv)) * blurP.w; }

void main(void) {
  float z0 = zOf(vUV);
  float thresh = 0.04 * z0 + 0.05;
  float sum = 0.0;
  float wsum = 0.0;
  for (int y = -1; y <= 2; y++) {
    for (int x = -1; x <= 2; x++) {
      vec2 o = vec2(float(x), float(y)) * blurP.xy;
      float w = max(0.0, 1.0 - abs(zOf(vUV + o) - z0) / thresh);
      sum += texture2D(aoTex, vUV + o).r * w;
      wsum += w;
    }
  }
  gl_FragColor = vec4(vec3(wsum > 0.0 ? sum / wsum : texture2D(aoTex, vUV).r), 1.0);
}
`;

// ---------------------------------------------------------------------------
// 3. THE PASS
// ---------------------------------------------------------------------------
// Meshes that must never enter the depth pre-pass. vegfar_/t2..t5 live past the
// fade distance and would only cost triangles; veg_grass is the shadow map's
// old lesson repeated — at half res a grass blade is sub-texel noise that the
// blur smears into a uniform grey wash over the whole ground, hiding the trunk
// and wall contacts that actually read; blobShadow is a fake shadow disc that
// would write depth 2 cm above the ground it is painted on.
// Water (sea/lake/river) is excluded for a third reason: it is a moving,
// re-levelled surface whose depth would flicker AO along every shoreline, and
// nothing that receives AO is ever behind it.
const SKIP_RE = /^(vegfar_|veg_grass|veg_shadow|blobShadow|sky|halo|haze|sunDisc|sea|lake|river|water|t[2-9]_)/;

export class WorldAO {
  constructor(scene, opts = {}) {
    this.scene = scene;
    const q = new URLSearchParams(location.search);
    // ?ao=0 turns the whole mechanism off (this is how the A/B control shots
    // are taken: same build, same streaming, one variable).
    this.enabled = q.get('ao') !== '0';
    // THE OUTDOOR STRENGTH IS SET BY THE TIGHTEST SCENE IN THE GATE, not by
    // taste. town_street is overcast — the sky IS the key there, so the ambient
    // lobe this pass multiplies is very nearly the whole pixel — and round-006
    // already renders it 8.5 luminance under its DayZ plate, which leaves a
    // shotguard tolerance of max(noise 1.5, 15% of 8.5) = 1.5 points of frame
    // luminance for the whole mechanism. Measured AO coverage on that scene
    // (frame-mean 0.98) puts 0.85 at roughly 1.9 points, i.e. over the line, and
    // 0.65 at roughly 1.4. The creases still lose a third of their fill at 0.65.
    this.strength = +(q.get('aoK') ?? opts.strength ?? 0.65);
    this.strengthI = +(q.get('aoKi') ?? opts.strengthInterior ?? 0.55);
    // VEGETATION GETS ITS OWN STRENGTH, and the reason is a measured defect
    // rather than a preference: veg_grass is deliberately kept OUT of the depth
    // pre-pass (sub-texel blades are the grey-wash lesson the shadow map
    // already paid for), so a grass pixel samples the occlusion computed for
    // the GROUND BEHIND IT. In forest_day that is most of the bottom third of
    // the frame, and at parity with the hard surfaces it took the ground band
    // 5.7 luminance and 12.3 p99 below round-006 — a gate failure bought on
    // pixels whose AO was never computed for them.
    this.strengthV = +(q.get('aoKv') ?? opts.strengthVeg ?? 0.2);
    this.radius = +(q.get('aoR') ?? opts.radius ?? 0.9);
    // THE BIAS IS THE GATE-CRITICAL CONSTANT, not the gain. Measured in the
    // izba, the MEAN occlusion over a whole frame moves 0.61 -> 0.41 -> 0.15 as
    // the bias goes 0.04 -> 0.06 -> 0.12, while the tightest creases barely
    // move: a low bias buys a uniform grey wash over every surface (which shows
    // up as frame luminance, i.e. as a regression against the DayZ plate) and
    // pays for it with almost no extra contact darkening. At 0.20 an open wall
    // is left at exactly 1.0 and the gain can be spent where geometry actually
    // occludes, which is both the honest AO and the only version town_street's
    // 1.5-point luminance tolerance can afford.
    this.intensity = +(q.get('aoI') ?? opts.intensity ?? 16);
    this.bias = +(q.get('aoB') ?? opts.bias ?? 0.20);
    this.floor = +(q.get('aoF') ?? opts.floor ?? 0.25);
    this.fade = [22, 36];
    this.reach = 40;
    this.ready = false;
    this.frame = 0;
    this.lastList = -1e9;
    this.lastCam = new Vector3(1e9, 1e9, 1e9);
    this.p = new Vector4(0, 0, 0, 0);
    this.uCam = new Vector4(0, 0, 0, 0);
    this.uAo = new Vector4(0, 0, 0, 0);
    this.uQ = new Vector4(0, 0, 0, 0);
    this.uBlur = new Vector4(0, 0, 0, 0);
    this.listCount = 0;
    scene.onBeforeRenderObservable.add(() => this.preRender());
    scene.onAfterRenderTargetsRenderObservable.add(() => this.bind());
  }

  // Deferred: createWorld() runs before the camera exists, and a DepthRenderer
  // without a camera renders nothing.
  init() {
    const scene = this.scene;
    const cam = scene.activeCamera;
    if (!cam || this.depth) return;
    const engine = scene.getEngine();
    this.w = Math.max(64, Math.floor(engine.getRenderWidth() / 2));
    this.h = Math.max(64, Math.floor(engine.getRenderHeight() / 2));

    // PACKED RGBA8 depth on purpose: a 32-bit packed byte target is the one
    // colour format every GL context here is guaranteed to render to, and it
    // carries ~0.7 mm of depth resolution over a 12 km frustum. Nothing in
    // this pass needs float, so nothing in it can fail on a capability check.
    this.depthRT = new RenderTargetTexture('ashAODepth', { width: this.w, height: this.h },
      scene, false, true, 0, false, Texture.NEAREST_SAMPLINGMODE);
    this.depth = new DepthRenderer(scene, 0, cam, false, Texture.NEAREST_SAMPLINGMODE,
      false, 'ashAODepth', this.depthRT);
    this.depthRT.refreshRate = 1;
    // customRenderTargets renders BEFORE the procedural-texture stage, so the
    // AO pass reads depth from this frame rather than trailing it by one.
    scene.customRenderTargets.push(this.depthRT);

    this.aoTex = new ProceduralTexture('ashAO', { width: this.w, height: this.h },
      { fragmentSource: AO_FRAG }, scene, null, false);
    this.aoTex.refreshRate = 1;
    this.aoTex.wrapU = Texture.CLAMP_ADDRESSMODE;
    this.aoTex.wrapV = Texture.CLAMP_ADDRESSMODE;
    this.aoTex.setTexture('depthTex', this.depthRT);

    this.blurTex = new ProceduralTexture('ashAOBlur', { width: this.w, height: this.h },
      { fragmentSource: BLUR_FRAG }, scene, null, false);
    this.blurTex.refreshRate = 1;
    this.blurTex.wrapU = Texture.CLAMP_ADDRESSMODE;
    this.blurTex.wrapV = Texture.CLAMP_ADDRESSMODE;
    this.blurTex.setTexture('aoTex', this.aoTex);
    this.blurTex.setTexture('depthTex', this.depthRT);
  }

  resizeIfNeeded() {
    const engine = this.scene.getEngine();
    const w = Math.max(64, Math.floor(engine.getRenderWidth() / 2));
    const h = Math.max(64, Math.floor(engine.getRenderHeight() / 2));
    if (w === this.w && h === this.h) return;
    this.w = w;
    this.h = h;
    this.depthRT.resize({ width: w, height: h });
    this.aoTex.resize({ width: w, height: h }, false);
    this.blurTex.resize({ width: w, height: h }, false);
  }

  // Leaf cards are alpha cut-outs and Babylon's depth pass only cuts them out
  // if the material advertises an alpha-test texture; a ShaderMaterial does
  // not. sunshadow.js teaches the shared veg materials to answer for its own
  // depth map — but only while the sun is up, and this pass needs it at every
  // hour. Same flag, same cut-off, so whichever system gets there first the
  // result is byte-identical and the other one's call is a no-op.
  static enableAlphaDepth(mat) {
    if (!mat || mat.__ashAlphaShadow) return;
    const tex = mat._textures && mat._textures.tex;
    if (!tex) return;
    mat.__ashAlphaShadow = true;
    mat.needAlphaTesting = () => true;
    mat.getAlphaTestTexture = () => tex;
    mat.alphaCutOff = 0.88;
  }

  refreshList(cp) {
    const list = [];
    const R = this.reach;
    for (const m of this.scene.meshes) {
      const n = m.name;
      if (SKIP_RE.test(n)) continue;
      if (!m.isEnabled() || m.isVisible === false) continue;
      if (!m.getTotalVertices || !m.getTotalVertices()) continue;
      if (m.infiniteDistance) continue;
      const bs = m.getBoundingInfo().boundingSphere;
      if (Vector3.Distance(bs.centerWorld, cp) - bs.radiusWorld > R) continue;
      if (n.indexOf('veg') === 0) WorldAO.enableAlphaDepth(m.material);
      list.push(m);
    }
    this.depthRT.renderList = list;
    this.listCount = list.length;
  }

  preRender() {
    if (!this.enabled) return;
    this.init();
    if (!this.depth) return;
    const cam = this.scene.activeCamera;
    if (!cam) return;
    this.frame++;
    this.resizeIfNeeded();

    const cp = cam.globalPosition || cam.position;
    // The near-field list only has to keep up with walking pace: a 40 m reach
    // against a 22-36 m fade means a mesh has ~4 m of slack before it can
    // matter, so 250 ms of staleness is invisible and the scan is not paid for
    // on every frame.
    const now = performance.now();
    if (now - this.lastList > 250 || Vector3.DistanceSquared(cp, this.lastCam) > 4) {
      this.lastList = now;
      this.lastCam.copyFrom(cp);
      this.refreshList(cp);
    }

    // Read the projection off the camera rather than off the scene: the scene
    // matrix is only refreshed inside _renderForCamera, i.e. AFTER this
    // observable, so on an ADS zoom the scene copy is a frame stale.
    const proj = cam.getProjectionMatrix().m;
    const n = cam.minZ;
    const f = cam.maxZ;
    this.uCam.set(n, f - n, 1 / proj[0], 1 / proj[5]);
    this.uAo.set(this.radius, this.intensity, this.bias, this.floor);
    this.uQ.set(this.fade[0], this.fade[1], 1 / this.w, 1 / this.h);
    this.uBlur.set(1 / this.w, 1 / this.h, n, f - n);
    this.aoTex.setVector4('camP', this.uCam);
    this.aoTex.setVector4('aoP', this.uAo);
    this.aoTex.setVector4('aoQ', this.uQ);
    this.blurTex.setVector4('blurP', this.uBlur);

    // Not "is the code installed" — is there a rendered AO frame to sample.
    this.ready = this.blurTex.isReady() && this.depthRT.isReady() && this.frame > 2;
  }

  bind() {
    if (!MATS.size || !this.blurTex) return;
    const engine = this.scene.getEngine();
    const on = this.enabled && this.ready;
    this.p.set(on ? this.strengthV : 0, 1 / engine.getRenderHeight(),
      on ? this.strength : 0, on ? this.strengthI : 0);
    const vp = this.scene.getTransformMatrix();
    for (const m of MATS) {
      m.setVector4('ashAOP', this.p);
      m.setMatrix('ashAOVP', vp);
      m.setTexture('ashAOMap', this.blurTex);
    }
  }
}

// Patch the world shaders + build the pass. Must run before any world material
// is constructed, and BEFORE installWorldShadows() so the two injections
// compose in a known order (this one first: sunshadow's `+ ambUp *` split then
// wraps the term this one wrote, which is why the fence/rail anchor survives).
export function installWorldAO(scene, opts) {
  if (installed) return scene.__ashAO;
  installed = true;
  for (const [k, edits] of Object.entries(AO_FRAGS)) patchFragment(k, edits);
  hookShaderMaterial();
  const sys = new WorldAO(scene, opts);
  sys.mats = MATS;
  sys.patched = PATCHED;
  scene.__ashAO = sys;
  window.__ashAO = sys;
  return sys;
}
